import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Chip, FormControl, TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { Store, useAppDispatch } from 'src/redux/Store';
import { ErrorBag } from 'src/models/ErrorModel';
import { FORM_ID } from 'src/models/FormModel';
import { getGatewayListAction } from 'src/redux/actions/gatewayAction';
import { ProcessingType } from 'src/models/GatewayModel';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    marginBottom: '8px',
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  minWidth: {
    minWidth: '480px',
  },
}));

interface Props {
  disabled?: boolean,
  form: FORM_ID,
  id?: string,
  label: string,
  margin?: 'dense' | 'normal' | undefined,
  name: string,
  noMarginTop?: boolean,
  noMinWidth?: boolean,
  onChange: (ids: number[]) => void,
  types: ProcessingType[],
  value?: number[],
}

interface DataItem {
  id: number,
  label: string,
}

const GatewayMultiSelector = (props: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const {
    disabled,
    form,
    id,
    label,
    margin,
    name,
    noMarginTop,
    noMinWidth,
    onChange,
    types,
    value,
  } = props;

  const gateways = useSelector((state: Store) => state.gateways);

  const errors: ErrorBag =
    useSelector((state: Store) => state.errors[form] || {} as ErrorBag);

  useEffect(() => {
    dispatch(getGatewayListAction());
  }, []);

  const getOptions = (): DataItem[] => gateways
    .filter((gateway) => !!gateway.processing_type && types.includes(gateway.processing_type))
    .map((gateway) => ({
      id: gateway.id as number,
      label: gateway.gateway_name,
    }))
    .sort((first, second) => first.label.localeCompare(second.label));

  const getSelectedOptions = (): DataItem[] => {
    const options = getOptions();
    return (value || [])
      .map((selectedId) => options.find((option) => option.id === selectedId))
      .filter((option): option is DataItem => !!option);
  };

  const onSelectionChange = (items: DataItem[]) => {
    onChange(items.map((item) => item.id));
  };

  const getClasses = () => {
    const noMarginTopClass = noMarginTop ? '' : classes.marginTop;
    const noMinWidthClass = noMinWidth ? '' : classes.minWidth;
    return `${noMarginTopClass} ${noMinWidthClass} ${classes.formControl}`;
  };

  return (
    <FormControl
      id={id}
      variant="outlined"
      className={classes.formControl}>
      <Autocomplete
        multiple
        disabled={disabled}
        className={getClasses()}
        value={getSelectedOptions()}
        onChange={(event, items) => onSelectionChange(items as DataItem[])}
        options={getOptions()}
        getOptionLabel={(option: DataItem) => option.label}
        getOptionSelected={(option, item) => option.id === item.id}
        renderOption={(option: DataItem) => (
          <span id={`${id}-option-${option.id}`}>{ option.label }</span>
        )}
        renderTags={(items: DataItem[], getTagProps) => items.map((item, index) => (
          <Chip
            {...getTagProps({ index })}
            key={item.id}
            label={item.label}
            size="small" />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={label}
            placeholder={label}
            error={!!errors[name]}
            helperText={errors[name] && errors[name].join(',')}
            margin={margin || 'normal'} />
        )} />
    </FormControl>
  );
};

export default GatewayMultiSelector;
